import Header from '../components/Header'
import Footer from '../components/Footer'
import {
  CreditCard,
  FileText,
  QrCode,
  ShoppingBag,
  CheckCircle,
  ArrowRight,
  Shield,
  Zap,
  Users,
  Lock,
  Smartphone,
  Wallet,
  Building2,
  Globe,
} from 'lucide-react'
import { Link } from 'react-router-dom'

const ProductsPage = () => {
  const paymentMethods = [
    'Visa', 'Mastercard', 'UnionPay', 'JazzCash',
    'EasyPaisa', 'NayaPay', 'SadaPay', 'Bank Transfer', 'Account Number',
  ]

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* ── Hero ── */}
      <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-24 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="container-max section-padding relative">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/20 border border-orange-500/30 rounded-full text-orange-400 text-sm font-medium mb-6">
              Our Products
            </div>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
              A dynamic platform for{' '}
              <span className="text-orange-500">every payment need</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Accept digital payments through payment gateway, payment links, bill payments, mobile QR,
              and powerful e-commerce integrations. Use MobiPay and experience the new era of payments.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/get-started"
                className="inline-flex items-center justify-center bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-orange-500/30"
              >
                Get Started Free <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center border border-gray-600 hover:border-orange-500 text-gray-300 hover:text-orange-400 px-8 py-4 rounded-lg font-semibold transition-all duration-200"
              >
                Talk to Sales
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Quick benefits bar ── */}
      <section className="border-b border-gray-200 bg-white">
        <div className="container-max section-padding py-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { icon: <Zap className="h-5 w-5 text-orange-500" />, title: 'Self Sign-Up', sub: 'Go live in minutes, no paperwork' },
              { icon: <Shield className="h-5 w-5 text-orange-500" />, title: 'Secure Payments', sub: 'Bank-level encryption on every transaction' },
              { icon: <Users className="h-5 w-5 text-orange-500" />, title: '24/7 Support', sub: 'Via email, chat, and phone' },
            ].map((b, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  {b.icon}
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">{b.title}</p>
                  <p className="text-gray-500 text-xs">{b.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────
          PRODUCT 1 — Payment Gateway
      ───────────────────────────────────────────── */}
      <section id="payment-gateway" className="py-24 bg-white">
        <div className="container-max section-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-50 text-orange-600 rounded-full text-sm font-medium">
                <CreditCard className="h-4 w-4" /> Payment Gateway
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
                A safe and secure checkout page for your business
              </h2>
              <p className="text-gray-500 text-lg leading-relaxed">
                MobiPay offers a safe and secure checkout page for e-commerce retailers, payment links,
                and a bill payments solution to accept payments digitally from customers. Allow customers
                to pay using payment cards, mobile wallets, and account numbers.
              </p>
              <ul className="space-y-3">
                {[
                  'Accept all major cards — Visa, Mastercard, UnionPay',
                  'Mobile wallets — JazzCash, EasyPaisa, NayaPay, SadaPay',
                  'Direct bank account / IBFT payments',
                  'Real-time transaction processing & reporting',
                  'Fraud detection & 3D Secure authentication',
                  'Sandbox testing environment included',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-600">
                    <CheckCircle className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/get-started" className="inline-flex items-center bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 shadow-md">
                Get Started <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>

            {/* Payment methods card */}
            <div className="bg-gray-900 rounded-3xl p-8 shadow-2xl">
              <p className="text-gray-400 text-sm mb-5 uppercase tracking-wide">Accepted Payment Methods</p>
              <div className="grid grid-cols-3 gap-3 mb-6">
                {paymentMethods.map((m, i) => (
                  <div key={i} className="bg-gray-800 border border-gray-700 rounded-xl py-3 px-2 text-center hover:border-orange-500 transition-colors">
                    <p className="text-white text-xs font-semibold">{m}</p>
                  </div>
                ))}
              </div>
              <div className="bg-gray-800 rounded-2xl p-5">
                <div className="flex justify-between items-center mb-3">
                  <p className="text-gray-400 text-xs">Order total</p>
                  <p className="text-white font-bold">₨ 12,500</p>
                </div>
                <div className="w-full bg-orange-500 text-white py-3 rounded-xl text-center font-bold text-sm">
                  Pay Now — Secured by MobiPay
                </div>
                <div className="flex items-center justify-center gap-2 mt-3">
                  <Lock className="h-3 w-3 text-gray-500" />
                  <p className="text-gray-500 text-xs">256-bit SSL encrypted</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────
          PRODUCT 2 — Bill Payments
      ───────────────────────────────────────────── */}
      <section id="bill-payments" className="py-24 bg-gray-50">
        <div className="container-max section-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Invoice card */}
            <div className="order-2 lg:order-1 bg-white rounded-3xl p-8 shadow-lg border border-gray-100">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-gray-500 text-sm">MobiPay Billing</p>
                  <p className="text-gray-900 font-bold text-lg">Invoice Portal</p>
                </div>
                <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center">
                  <FileText className="h-5 w-5 text-white" />
                </div>
              </div>
              {[
                { id: '#1023', client: 'ABC Store', amount: '₨ 45,000', status: 'Paid', color: 'bg-green-100 text-green-700' },
                { id: '#1024', client: 'XYZ Traders', amount: '₨ 12,000', status: 'Pending', color: 'bg-yellow-100 text-yellow-700' },
                { id: '#1025', client: 'Tech Co.', amount: '₨ 78,500', status: 'Paid', color: 'bg-green-100 text-green-700' },
                { id: '#1026', client: 'Retail Hub', amount: '₨ 9,800', status: 'Overdue', color: 'bg-red-100 text-red-700' },
              ].map((inv, i) => (
                <div key={i} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                  <div>
                    <p className="text-gray-900 font-semibold text-sm">{inv.id} — {inv.client}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${inv.color}`}>{inv.status}</span>
                    <span className="text-gray-900 font-bold text-sm">{inv.amount}</span>
                  </div>
                </div>
              ))}
              <div className="mt-6 grid grid-cols-3 gap-3 text-center text-xs">
                {['Mobile Banking', 'Internet Banking', 'Over-the-Counter'].map((ch) => (
                  <div key={ch} className="bg-orange-50 border border-orange-100 rounded-lg py-2 px-1 text-orange-700 font-medium">
                    {ch}
                  </div>
                ))}
              </div>
            </div>

            <div className="order-1 lg:order-2 space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-50 text-orange-600 rounded-full text-sm font-medium">
                <FileText className="h-4 w-4" /> Bill Payments
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
                Allow us to take care of Bill Payments
              </h2>
              <p className="text-gray-500 text-lg leading-relaxed">
                Billing made hassle-free. MobiPay Billing is made for companies of all sizes to create
                and issue invoices and collect payments and fees through any bank's mobile banking,
                internet banking, and over-the-counter locations.
              </p>
              <ul className="space-y-3">
                {[
                  'Create branded invoices in seconds',
                  'Collect through all major banks & wallets',
                  'Mobile banking, internet banking & OTC',
                  'Automated payment reminders & dunning',
                  'Real-time payment status tracking',
                  'Detailed reconciliation reports',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-600">
                    <CheckCircle className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/get-started" className="inline-flex items-center bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 shadow-md">
                Start Billing <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────
          PRODUCT 3 — Mobile Payments
      ───────────────────────────────────────────── */}
      <section id="mobile-payments" className="py-24 bg-gray-900 text-white">
        <div className="container-max section-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/20 border border-orange-500/30 text-orange-400 rounded-full text-sm font-medium">
                <QrCode className="h-4 w-4" /> Mobile Payments
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold">
                Enabling QR and Tap &amp; Pay transactions throughout Pakistan
              </h2>
              <p className="text-gray-300 text-lg leading-relaxed">
                We've partnered with UnionPay International, the world's largest payment scheme,
                to facilitate mobile payments. Accept QR code payments and contactless Tap &amp; Pay
                transactions at your business.
              </p>
              <ul className="space-y-3">
                {[
                  'QR code payments — scan and pay instantly',
                  'Tap & Pay with NFC-enabled devices',
                  'UnionPay International partnership',
                  'Works across all major mobile wallets',
                  'In-store and online mobile payments',
                  'Instant settlement to your account',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-300">
                    <CheckCircle className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/get-started" className="inline-flex items-center bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition-all shadow-lg shadow-orange-500/30">
                Enable Mobile Payments <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>

            <div className="space-y-4">
              {/* QR demo card */}
              <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700 text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-orange-500 rounded-3xl mb-4">
                  <QrCode className="h-10 w-10 text-white" />
                </div>
                <p className="text-white font-bold text-lg">Scan to Pay</p>
                <p className="text-gray-400 text-sm mt-1">Point camera at a MobiPay QR code</p>
              </div>
              {/* Wallet support grid */}
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: <Smartphone className="h-6 w-6 text-orange-400" />, name: 'JazzCash', type: 'Mobile Wallet' },
                  { icon: <Smartphone className="h-6 w-6 text-orange-400" />, name: 'EasyPaisa', type: 'Mobile Wallet' },
                  { icon: <Wallet className="h-6 w-6 text-orange-400" />, name: 'NayaPay', type: 'Digital Bank' },
                  { icon: <Wallet className="h-6 w-6 text-orange-400" />, name: 'SadaPay', type: 'Digital Bank' },
                ].map((w, i) => (
                  <div key={i} className="bg-gray-800 border border-gray-700 rounded-xl p-4 flex items-center gap-3 hover:border-orange-500 transition-colors">
                    <div className="w-10 h-10 bg-gray-700 rounded-lg flex items-center justify-center">
                      {w.icon}
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">{w.name}</p>
                      <p className="text-gray-400 text-xs">{w.type}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-orange-500/10 border border-orange-500/20 rounded-xl p-4 text-center">
                <p className="text-orange-400 font-semibold text-sm">Powered by UnionPay International</p>
                <p className="text-gray-400 text-xs mt-1">World's largest payment scheme</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────
          PRODUCT 4 — E-Commerce Plugins
      ───────────────────────────────────────────── */}
      <section id="ecommerce" className="py-24 bg-white">
        <div className="container-max section-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Plugins grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {[
                { name: 'WooCommerce', bg: 'bg-purple-50 border-purple-200', text: 'text-purple-700', icon: <Globe className="h-6 w-6 text-purple-500" />, desc: 'WordPress' },
                { name: 'Shopify', bg: 'bg-green-50 border-green-200', text: 'text-green-700', icon: <ShoppingBag className="h-6 w-6 text-green-500" />, desc: 'App' },
                { name: 'Magento', bg: 'bg-orange-50 border-orange-200', text: 'text-orange-700', icon: <ShoppingBag className="h-6 w-6 text-orange-500" />, desc: 'Extension' },
                { name: 'OpenCart', bg: 'bg-blue-50 border-blue-200', text: 'text-blue-700', icon: <ShoppingBag className="h-6 w-6 text-blue-500" />, desc: 'Module' },
                { name: 'REST API', bg: 'bg-gray-50 border-gray-200', text: 'text-gray-700', icon: <Zap className="h-6 w-6 text-gray-500" />, desc: 'Custom' },
                { name: 'iOS & Android', bg: 'bg-red-50 border-red-200', text: 'text-red-700', icon: <Smartphone className="h-6 w-6 text-red-500" />, desc: 'SDK' },
              ].map((p, i) => (
                <div key={i} className={`${p.bg} border rounded-2xl p-5 text-center hover:shadow-md transition-all duration-200 group`}>
                  <div className="flex justify-center mb-2">{p.icon}</div>
                  <p className={`font-bold text-sm ${p.text}`}>{p.name}</p>
                  <p className="text-gray-400 text-xs mt-0.5">{p.desc}</p>
                </div>
              ))}
            </div>

            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-50 text-orange-600 rounded-full text-sm font-medium">
                <ShoppingBag className="h-4 w-4" /> E-Commerce Plugins
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
                We're Pro-Developers on the most popular E-commerce plugins
              </h2>
              <p className="text-gray-500 text-lg leading-relaxed">
                Delivering powerful, adaptable, and customizable payments integration. Whether you're
                on WooCommerce, Shopify, or building a custom solution with our REST API, we have you covered.
              </p>
              <ul className="space-y-3">
                {[
                  'One-click plugin installation',
                  'Full sandbox & testing environment',
                  'Comprehensive developer documentation',
                  'Dedicated developer support',
                  'Webhook support for real-time events',
                  'Secure, encrypted integration',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-600">
                    <CheckCircle className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/get-started" className="inline-flex items-center bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 shadow-md">
                View Developer Docs <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Partners ── */}
      <section className="py-20 bg-gray-50">
        <div className="container-max section-padding text-center">
          <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-3">Integrated with Pakistan's Banking Ecosystem</h2>
          <p className="text-gray-500 mb-12">All major banks and wallets supported out of the box.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-10">
            {['HBL', 'MCB', 'UBL', 'Meezan Bank', 'Allied Bank', 'Bank Alfalah', 'Faysal Bank', 'Bank Al-Habib', 'Standard Chartered', 'Habib Metro', 'NBP', 'Askari Bank'].map((bank, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-xl py-3 px-2 text-center hover:border-orange-300 hover:bg-orange-50 transition-all duration-200">
                <Building2 className="h-5 w-5 text-orange-400 mx-auto mb-1" />
                <p className="text-gray-700 font-semibold text-xs">{bank}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-xl mx-auto">
            {['JazzCash', 'EasyPaisa', 'NayaPay', 'SadaPay'].map((w, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-xl py-4 px-3 text-center hover:border-orange-300 hover:bg-orange-50 transition-all duration-200">
                <Wallet className="h-6 w-6 text-orange-400 mx-auto mb-2" />
                <p className="text-gray-700 font-bold text-sm">{w}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-orange-500/5" />
        <div className="container-max section-padding relative text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">Ready to get started?</h2>
          <p className="text-gray-300 text-lg mb-8 max-w-xl mx-auto">
            Self sign-up and go live in minutes. No setup fees, no paperwork.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/get-started"
              className="inline-flex items-center justify-center bg-orange-500 hover:bg-orange-600 text-white px-10 py-4 rounded-lg font-bold transition-all duration-200 shadow-xl shadow-orange-500/30"
            >
              Join Now <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center border border-gray-600 hover:border-orange-500 text-gray-300 hover:text-orange-400 px-10 py-4 rounded-lg font-semibold transition-all duration-200"
            >
              Contact Sales
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default ProductsPage
